// @flow

import React, { type Node } from 'react';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useTranslation } from 'react-i18next';

import { TextField } from '../../../../../atoms';
import { ErrorLabel } from '../../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

type Props = {
  rootStyle?: {},
  fieldName: string,
};

export function FieldStreetNumber({ rootStyle, fieldName }: Props): Node {
  const inputId = useUniqueId();
  const [value, handleChange] = useUserInputAsTextField(fieldName);
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError(fieldName);
  const submitting = useSubmitting();
  const incomingPayment = useIncomingPayment();
  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('street_number');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  const country = getCountry(incomingPayment);

  return (
    <div style={rootStyle} data-test-name="address-street-number">
      <label htmlFor={inputId}>
        <StreetNumberLabel />
      </label>

      <TextField
        type="text"
        id={inputId}
        value={value}
        onChange={handleChange}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        autoComplete="off"
        name="address-street-number"
      />

      {formHasBeenSent && validationError && (
        <ValidationMessageByCountry validationError={validationError} country={country}/>
      )}
    </div>
  );
}

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
  country: string,
|};

function ValidationMessageByCountry({ validationError, country }: ValidationMessageProps): Node {
  switch (country) {
    case 'co':
    case 'pe':
    case 'cl':
    case 'uy':
      return <ValidationMessagesSpanish validationError={validationError} />;

    default:
      throw new Error(`Field Street Number: error messages not mapped for country: ${country}`);
  }
}

type ValidationMessageForLanguageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessagesSpanish({ validationError }: ValidationMessageForLanguageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return (
        <ErrorLabel>
          Es obligatorio informar el número del domicilio
        </ErrorLabel>
      );

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          El número del domicilio es inválido
        </ErrorLabel>
      );
  }
}

function StreetNumberLabel(): Node {
  const { t } = useTranslation('opened-page');
  return t('volcanes.form.streetNumber.label');
}
